import {
  dateSchema,
  dateTypeSchema,
  emailSchema,
  languageSchema,
  nameSchema,
  objectIdSchema,
  paginationWithSearchSchema,
  phoneSchema,
  partySchema,
  Ticket,
  ticketCategorySchema,
  ticketPrioritySchema,
  ticketSourceSchema,
  ticketStatusSchema,
  UTagSharedSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

/**
 * Array filter arriving over a query string.
 *
 * The admin panel serializes GET params with `String(value)`, so
 * `['open','in_progress']` reaches us as the single string
 * `"open,in_progress"`. A one-element filter arrives as a bare value.
 * Accept all three shapes — the alternative is every caller having to
 * know how the serializer flattens arrays.
 */
const queryArray = <T extends z.ZodTypeAny>(inner: T) =>
  z.preprocess(val => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      const parts = val.split(',').map(s => s.trim()).filter(Boolean);
      return parts.length > 0 ? parts : undefined;
    }
    return val == null || val === '' ? undefined : [val];
  }, z.array(inner));

/**
 * Boolean from a query string. `z.coerce.boolean()` is wrong here —
 * it runs `Boolean("false")`, which is `true`, so a `?unassigned=false`
 * filter would silently invert. Same treatment as `route.api.ts`.
 */
const queryBoolean = z.preprocess(
  v => (v === 'true' ? true : v === 'false' ? false : v),
  z.boolean(),
);

// ─── filter-only schema (list endpoints + future agent tools compose from this) ──

export const ticketsQueryAPISchema = z.object({
  status: ticketStatusSchema
    .optional()
    .describe(
      'Filter by lifecycle status (`open`, `in_progress`, `waiting_on_customer`, `resolved`, `closed`).',
    ),
  statusIn: queryArray(ticketStatusSchema)
    .optional()
    .describe(
      'Filter to tickets whose status is ANY of these values (Mongo `$in`). Use for buckets — the inbox "Needs us" tab passes `["open", "in_progress"]`. Ignored when `status` is also set.',
    ),
  priority: ticketPrioritySchema
    .optional()
    .describe('Filter by priority (`low`, `normal`, `high`, `urgent`).'),
  category: ticketCategorySchema
    .optional()
    .describe(
      'Filter by what the ticket is about (`order`, `waybill`, `payment`, `account`, `integration`, `app_issue`, `feature_request`, `complaint`, `other`).',
    ),
  source: ticketSourceSchema
    .optional()
    .describe(
      'Filter by how the ticket reached Hero (`ai_whatsapp`, `ai_app`, `admin_manual`, `dashboard`, `phone`, `email`, `other`).',
    ),
  assignedToId: objectIdSchema
    .optional()
    .describe(
      'Filter to tickets owned by this AdminUser (`assignedTo._id`). Resolve from `search_admin_users` when the user names a staff member.',
    ),
  unassigned: queryBoolean
    .optional()
    .describe('When true, return only tickets nobody has picked up (`assignedTo` unset).'),
  orgId: objectIdSchema
    .optional()
    .describe('Filter to tickets raised by users of this Org (`org._id`).'),
  orderId: objectIdSchema
    .optional()
    .describe('Filter to tickets about a specific order (`order._id`).'),
  conversationId: objectIdSchema
    .optional()
    .describe(
      'Filter to tickets raised out of one chat thread (`conversation._id`). Used to check whether a thread already has an open ticket before opening another.',
    ),
  phone: phoneSchema
    .optional()
    .describe('Filter by the requester\'s phone (`requester.phone`).'),
  simpleDate: dateTypeSchema
    .optional()
    .describe("Window filter on `doc.createdAt`: 'today', 'this_week', 'this_month', etc."),
  createdFrom: dateSchema
    .optional()
    .describe('Filter to tickets created on or after this date (`doc.createdAt >=`).'),
  createdTo: dateSchema
    .optional()
    .describe('Filter to tickets created on or before this date (`doc.createdAt <=`).'),
  tags: queryArray(z.string())
    .optional()
    .describe('Filter to tickets whose `UTags[].code` matches any value in this list.'),
});
export type TicketsQueryAPI = z.infer<typeof ticketsQueryAPISchema>;

// ─── get-all (admin inbox) ────────────────────────────────────────────────────

export const ticketGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  ...ticketsQueryAPISchema.shape,
});
export type TicketGetAllAdminAPI = API<z.infer<typeof ticketGetAllAdminAPISchema>, Ticket[]>;

// ─── counts per status (inbox tab badges) ─────────────────────────────────────

export const ticketCountsAPISchema = ticketsQueryAPISchema.omit({
  status: true,
  statusIn: true,
});
export type TicketCountsAPI = API<
  z.infer<typeof ticketCountsAPISchema>,
  Record<z.infer<typeof ticketStatusSchema> | 'all', number>
>;

// ─── get-one ──────────────────────────────────────────────────────────────────

export const ticketGetOneAPISchema = z.object({
  _id: objectIdSchema.describe('`Ticket._id` (24-char Mongo ObjectId).'),
});
export type TicketGetOneAPI = API<z.infer<typeof ticketGetOneAPISchema>, Ticket>;

// ─── create ───────────────────────────────────────────────────────────────────

/**
 * Manual create — an admin logging a phone call, or a dashboard user
 * reporting a problem. The AI does NOT go through this shape; it calls
 * `TicketService.createFromAgent`, which fills the requester and the
 * conversation link from the thread it is already holding.
 */
export const ticketCreateAPISchema = z.object({
  subject: nameSchema.describe('One line the inbox can be scanned by.'),
  description: z
    .string()
    .trim()
    .optional()
    .describe("What is wrong, in the requester's own words where possible."),
  category: ticketCategorySchema
    .default('other')
    .describe('What the ticket is about. Defaults to `other`.'),
  priority: ticketPrioritySchema.default('normal').describe('How urgent. Defaults to `normal`.'),
  source: ticketSourceSchema
    .default('admin_manual')
    .describe('How it reached Hero. Defaults to `admin_manual`.'),
  requesterName: nameSchema.optional().describe('Name of the person who needs help.'),
  requesterPhone: phoneSchema.optional().describe('Callback number for the requester.'),
  requesterEmail: emailSchema.optional().describe('Email for the requester.'),
  requesterUserId: objectIdSchema
    .optional()
    .describe('`User._id` when the requester is a known account — the backend snapshots the party.'),
  language: languageSchema.optional().describe('Language to answer in (`ar` / `en` / `ur`).'),
  orgId: objectIdSchema
    .optional()
    .describe('Org the requester belongs to — the backend snapshots the party.'),
  orderId: objectIdSchema
    .optional()
    .describe('Order the ticket is about — the backend snapshots the ref.'),
  conversationId: objectIdSchema
    .optional()
    .describe('Chat thread this came out of, when the ticket is logged from a conversation.'),
  assignedToId: objectIdSchema
    .optional()
    .describe('AdminUser to own it from the start. Omit to leave it in the unassigned queue.'),
  UTags: z
    .array(UTagSharedSchema)
    .optional()
    .describe('Universal tag snapshots (`{ _id, name, code }`).'),
});
export type TicketCreateAPI = API<z.infer<typeof ticketCreateAPISchema>, Ticket>;

// ─── update ───────────────────────────────────────────────────────────────────

export const ticketUpdateAPISchema = z.object({
  _id: objectIdSchema.describe('`Ticket._id` to update.'),
  subject: nameSchema.optional(),
  description: z.string().trim().optional(),
  status: ticketStatusSchema
    .optional()
    .describe(
      'New lifecycle status. The backend stamps `resolvedAt` / `closedAt` on the transitions into `resolved` / `closed`, and appends a `status_changed` timeline row.',
    ),
  priority: ticketPrioritySchema.optional(),
  category: ticketCategorySchema.optional(),
  UTags: z.array(UTagSharedSchema).optional(),
  /**
   * Tell the requester, on closing. Only honoured together with
   * `status: 'resolved' | 'closed'` and a requester phone; ignored
   * otherwise. Sends the `ticket_closed` WhatsApp template on the line
   * the ticket's conversation runs on (best-effort — a send failure never
   * blocks the close), records the note as a non-internal ticket note,
   * and appends a `requester_notified` timeline row.
   */
  notify: z
    .object({
      note: z
        .string()
        .trim()
        .max(600)
        .optional()
        .describe(
          'What was done, in the requester\'s language — the body of the template. Blank sends the default closing line.',
        ),
      sendMessage: z
        .boolean()
        .default(true)
        .describe(
          'Send the `ticket_closed` WhatsApp template. `false` closes (and hands back, when asked) without messaging the requester; the note is kept as an internal note.',
        ),
      handBackToAi: z
        .boolean()
        .default(true)
        .describe(
          'Also release the linked conversation from human mode (`responderMode: ai`), with the note as the hand-back record the assistant reads first. No-op when the thread is not in human mode.',
        ),
    })
    .optional()
    .describe('Notify the requester and optionally hand the thread back — see docs/ai/human-takeover.md.'),
});
export type TicketUpdateAPI = API<z.infer<typeof ticketUpdateAPISchema>, Ticket>;

// ─── assign ───────────────────────────────────────────────────────────────────

export const ticketAssignAPISchema = z.object({
  _id: objectIdSchema.describe('`Ticket._id` to assign.'),
  assignedToId: objectIdSchema
    .optional()
    .describe('AdminUser `_id` to own this ticket. Omit to put it back in the unassigned queue.'),
});
export type TicketAssignAPI = API<z.infer<typeof ticketAssignAPISchema>, Ticket>;

// ─── notes ────────────────────────────────────────────────────────────────────

export const ticketAddNoteAPISchema = z.object({
  _id: objectIdSchema.describe('`Ticket._id` to append to.'),
  body: z.string().trim().min(1).describe('The note text.'),
  internal: z
    .boolean()
    .default(true)
    .describe(
      'Internal staff-only working note (default) vs. a customer-visible reply. A non-internal note on a ticket with a linked conversation is also SENT on that thread as a staff message (session text, inside the 24h WhatsApp window) and takes the thread over — see docs/ai/human-takeover.md. Outside the window the note is recorded but the send is refused (`whatsapp_outside_session_window`); close the ticket with a note to use the approved template instead.',
    ),
});
export type TicketAddNoteAPI = API<z.infer<typeof ticketAddNoteAPISchema>, Ticket>;

// ─── delete ───────────────────────────────────────────────────────────────────

export const ticketDeleteAPISchema = z.object({
  _id: objectIdSchema.describe('`Ticket._id` to delete.'),
});
export type TicketDeleteAPI = API<z.infer<typeof ticketDeleteAPISchema>, Ticket>;
